import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { CheckCircle2, MessageCircle, Mail, HelpCircle, ArrowRight } from "lucide-react";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_vee-trading-hub/artifacts/k4o9jcwu_VEE%20CONSULTANCY%20LOGO.png";
const COMMUNITY_URL = "https://chat.whatsapp.com/JxkaG8PAWl62gzPvgMedLL";
const AUTO_REDIRECT_SECS = 0;

export default function ThankYouPage() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const userName = params.get("name") || "Trader";
  const [countdown, setCountdown] = useState(AUTO_REDIRECT_SECS);
  const [redirectCancelled, setRedirectCancelled] = useState(false);

  useEffect(() => {
    // Fire Meta Pixel Lead event
    try {
      if (typeof window !== "undefined" && window.fbq) {
        window.fbq("track", "Lead", { content_name: "Webinar Registration Confirmed" });
      }
    } catch (_e) { /* noop */ }
    window.scrollTo(0, 0);
  }, []);

  // Auto-redirect countdown → open WhatsApp community
  useEffect(() => {
    if (redirectCancelled) return;
    if (countdown <= 0) {
      try {
        if (typeof window !== "undefined" && window.fbq) {
          window.fbq("trackCustom", "AutoRedirectToCommunity");
        }
      } catch (_e) { /* noop */ }
      window.open(COMMUNITY_URL, "_blank");
      setRedirectCancelled(true);
      return;
    }
    const timer = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown, redirectCancelled]);

  return (
    <div
      data-testid="thank-you-page"
      className="min-h-screen bg-[#03060D] text-slate-200 font-body"
    >
      {/* Top nav */}
      <nav className="sticky top-0 z-50 backdrop-blur-xl bg-[#03060D]/80 border-b border-slate-800">
        <div className="max-w-5xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="VEE Consultancy" className="h-10 w-auto object-contain" />
          </Link>
          <Link
            to="/"
            className="text-xs font-mono uppercase tracking-wider text-slate-400 hover:text-emerald-400 transition-colors"
          >
            ← Back to Home
          </Link>
        </div>
      </nav>

      {/* Hero */}
      <section className="max-w-5xl mx-auto px-4 md:px-8 pt-12 md:pt-16 pb-8 text-center">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-500/10 border border-amber-500/40 mb-6 animate-pulse">
          <CheckCircle2 className="w-4 h-4 text-amber-400" />
          <span className="text-xs font-mono uppercase tracking-[0.2em] text-amber-300 font-bold">
            STEP 1 of 2 COMPLETE
          </span>
        </div>

        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-4">
          Welcome, <span className="text-emerald-400">{userName}</span>! 🎉
        </h1>
        <p className="font-display text-xl sm:text-2xl text-white mb-3">
          You Have Successfully Booked Your Seat
        </p>
        <p className="text-base sm:text-lg text-amber-400 font-bold mb-2">
          "Time Cycle Masterclass"
        </p>
        <p className="text-base text-slate-400 italic mt-6">Here Are Your Next Steps...</p>
      </section>

      {/* 🔥 HERO CTA — Big Prominent WhatsApp Join Button (impossible to miss on mobile) */}
      <section className="max-w-3xl mx-auto px-4 md:px-8 pb-6 -mt-2">
        {/* Auto-redirect countdown notice */}
        {!redirectCancelled && countdown > 0 && (
          <div className="mb-3 rounded-lg bg-amber-500/15 border border-amber-500/40 px-4 py-2 flex items-center justify-between gap-3 flex-wrap">
            <p className="text-amber-200 text-xs md:text-sm font-semibold">
              ⏱️ Opening WhatsApp Community in <span className="text-white font-black text-base md:text-lg">{countdown}</span> sec...
            </p>
            <button
              onClick={() => setRedirectCancelled(true)}
              className="text-amber-300 hover:text-amber-100 text-[10px] md:text-xs underline"
              data-testid="cancel-auto-redirect"
            >
              Cancel Auto-Open
            </button>
          </div>
        )}
        {redirectCancelled && countdown <= 0 && (
          <div className="mb-3 rounded-lg bg-emerald-500/15 border border-emerald-500/40 px-4 py-2">
            <p className="text-emerald-200 text-xs md:text-sm font-semibold">
              ✅ WhatsApp Community opened in new tab. Agar close ho gaya, neeche button dabao.
            </p>
          </div>
        )}
        <a
          href={COMMUNITY_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="hero-join-whatsapp-btn"
          onClick={() => {
            try {
              if (typeof window !== "undefined" && window.fbq) {
                window.fbq("trackCustom", "JoinCommunityHeroCTA");
              }
            } catch (_e) { /* noop */ }
          }}
          className="block group"
        >
          <div className="relative overflow-hidden rounded-2xl border-2 border-emerald-400/60 bg-gradient-to-br from-emerald-600 via-emerald-500 to-emerald-700 p-5 md:p-6 shadow-[0_15px_50px_rgba(16,185,129,0.4)] group-hover:shadow-[0_20px_60px_rgba(16,185,129,0.6)] group-hover:scale-[1.02] transition-all">
            {/* Pulsing background layer for attention */}
            <div className="absolute inset-0 bg-white/10 animate-pulse pointer-events-none" />
            {/* Fire emoji floating */}
            <div className="relative flex flex-col items-center text-center">
              <div className="inline-flex items-center gap-2 mb-3 px-3 py-1 bg-rose-600 rounded-full text-xs font-black text-white animate-bounce">
                🔥 ACTION REQUIRED
              </div>
              <h2 className="text-white font-black text-2xl md:text-4xl leading-tight tracking-tight mb-2">
                JOIN NOW FOR
                <br />
                <span className="text-yellow-200">TOMORROW&apos;S LIVE</span>
                <br />
                TRADING SESSION
              </h2>
              <p className="text-emerald-100 text-sm md:text-base font-semibold mb-4">
                with your Mentor <span className="text-white font-black uppercase">Veenu Vaswani</span>
              </p>
              <div className="inline-flex items-center justify-center gap-3 bg-white text-emerald-700 font-black text-base md:text-xl px-6 md:px-8 py-3 md:py-4 rounded-xl shadow-[0_8px_20px_rgba(0,0,0,0.3)] group-hover:bg-yellow-100 transition-colors">
                <MessageCircle className="w-6 h-6 md:w-7 md:h-7" />
                <span>JOIN WHATSAPP COMMUNITY</span>
              </div>
              <p className="text-emerald-100 text-xs md:text-sm mt-3 font-semibold">
                ⚠️ Zoom link SIRF community me milega — Miss mat karo!
              </p>
            </div>
          </div>
        </a>
      </section>

      {/* 3-Step Cards */}
      <section className="max-w-5xl mx-auto px-4 md:px-8 pb-16">
        <div className="grid md:grid-cols-3 gap-5 md:gap-6">
          {/* STEP 1 — WhatsApp Community */}
          <div
            className="rounded-2xl border border-emerald-500/30 bg-gradient-to-b from-emerald-500/10 to-slate-900/40 p-6 md:p-7 flex flex-col"
            data-testid="step-whatsapp-card"
          >
            <div className="text-xs font-mono uppercase tracking-[0.3em] text-emerald-400 mb-3">
              STEP #1
            </div>
            <div className="w-16 h-16 rounded-2xl bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center mb-5 mx-auto">
              <MessageCircle className="w-8 h-8 text-emerald-400" />
            </div>
            <h3 className="font-display text-xl text-white text-center font-bold mb-3">
              Join Our Exclusive
              <br />
              <span className="text-emerald-400">WhatsApp Community</span>
            </h3>
            <p className="text-sm text-slate-300 text-center leading-relaxed mb-4 flex-grow">
              Right Here! Join us to get info on upcoming sessions, study material, and updates
              from <span className="text-white font-semibold">Team VEE Consultancy</span>.
            </p>
            <a
              href={COMMUNITY_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="join-whatsapp-thank-you-btn"
              className="inline-flex items-center justify-center gap-2 w-full h-12 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-[#03060D] font-bold transition-colors shadow-lg shadow-emerald-500/30"
            >
              <MessageCircle className="w-4 h-4" />
              Join WhatsApp Community
            </a>
          </div>

          {/* STEP 2 — Email */}
          <div
            className="rounded-2xl border border-slate-800 bg-slate-900/40 p-6 md:p-7 flex flex-col"
            data-testid="step-email-card"
          >
            <div className="text-xs font-mono uppercase tracking-[0.3em] text-slate-400 mb-3">
              STEP #2
            </div>
            <div className="w-16 h-16 rounded-2xl bg-amber-500/20 border border-amber-500/40 flex items-center justify-center mb-5 mx-auto">
              <Mail className="w-8 h-8 text-amber-400" />
            </div>
            <h3 className="font-display text-xl text-white text-center font-bold mb-3">
              Check Your
              <br />
              <span className="text-amber-400">WhatsApp & Email</span>
            </h3>
            <p className="text-sm text-slate-300 text-center leading-relaxed mb-6 flex-grow">
              Aapko WhatsApp pe confirmation message bhej diya gaya hai. Joining link aapko
              session se <span className="text-white font-semibold">1 ghanta pehle</span> bhej
              denge.
            </p>
            <div className="text-center text-xs font-mono uppercase tracking-widest text-slate-500 py-3 border border-slate-800 rounded-xl">
              ⏰ Stay Tuned
            </div>
          </div>

          {/* STEP 3 — Contact */}
          <div
            className="rounded-2xl border border-slate-800 bg-slate-900/40 p-6 md:p-7 flex flex-col"
            data-testid="step-contact-card"
          >
            <div className="text-xs font-mono uppercase tracking-[0.3em] text-slate-400 mb-3">
              STEP #3
            </div>
            <div className="w-16 h-16 rounded-2xl bg-blue-500/20 border border-blue-500/40 flex items-center justify-center mb-5 mx-auto">
              <HelpCircle className="w-8 h-8 text-blue-400" />
            </div>
            <h3 className="font-display text-xl text-white text-center font-bold mb-3">
              Got Questions?
              <br />
              <span className="text-blue-400">We're Here</span>
            </h3>
            <p className="text-sm text-slate-300 text-center leading-relaxed mb-6 flex-grow">
              Webinar ya{" "}
              <span className="italic text-white">"Time Cycle Masterclass"</span> ke baare mein
              koi sawaal hai? Hamare team ko email karein —
              <br />
              <a
                href="mailto:info@veeconsultancy.in"
                className="text-emerald-400 hover:text-emerald-300 font-semibold inline-block mt-2 break-all"
              >
                info@veeconsultancy.in
              </a>
            </p>
            <Link
              to="/contact"
              data-testid="contact-page-link"
              className="inline-flex items-center justify-center gap-2 w-full h-12 rounded-xl bg-slate-800 hover:bg-slate-700 text-white font-bold transition-colors border border-slate-700"
            >
              Contact Page
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>

        {/* Reassurance footer */}
        <div className="mt-12 p-6 rounded-2xl border border-emerald-500/20 bg-emerald-500/5 text-center">
          <p className="text-sm text-slate-300">
            <CheckCircle2 className="w-4 h-4 inline-block text-emerald-400 mr-2" />
            Your seat is locked. Aapka data safe hai. Webinar reminder WhatsApp pe milega.
          </p>
        </div>

        {/* SEBI Disclaimer */}
        <p className="mt-8 text-xs text-slate-500 text-center max-w-2xl mx-auto leading-relaxed">
          <strong className="text-slate-400">Disclaimer:</strong> VEE Consultancy purely educational
          content provide karta hai. Hum SEBI registered investment advisor nahi hain. Trading
          decisions market risk ke saath aate hain — kripya apni research ke saath hi koi bhi
          financial decision lein.
        </p>
      </section>
    </div>
  );
}
